import React, { createContext, useContext, useEffect, useState } from "react";
import { enviroment } from "./enviroment";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "./data/clients/ApiClient";
import { WhiteLabelConfigurations } from "./core/models/entities/Profile";
import { useProfileContext } from "./contexts/ProfileContext";

interface WhiteLabelProviderContext {
  whiteLabel: WhiteLabelConfigurations;
  updateWhiteLabelContext: (value: WhiteLabelConfigurations) => void;
  reset: () => void;
}

const WhiteLabelContext = createContext<WhiteLabelProviderContext | undefined>(
  undefined
);

interface RepositoriesProviderProps {
  children: React.ReactNode;
}

function WhiteLabelProvider({
  children,
}: RepositoriesProviderProps): React.ReactElement {
  const { profile } = useProfileContext();
  const apiClient = useApiClient();

  // Server-overstyret globalt brand (ADMIN -> Min profil -> Brand): laeses
  // een gang efter login og deles med brand-editoren via samme query-noegle,
  // saa en gemt aendring slaar igennem i header/footer med det samme.
  // Fejl/uafklaret -> enviroment-defaults (i dag Jet Sport) - chromen kan
  // aldrig blokere paa dette kald.
  const { data: serverBrand } = useQuery(
    ["brandConfig"],
    () => apiClient.getBrandConfig(),
    { enabled: !!profile, staleTime: 60000, retry: 1 },
  );

  function defaultConfigurations(): WhiteLabelConfigurations {
    // Standard-brandet kommer fra enviroment.brand* (rebranding 26/8): i dag
    // Jet Sport, paa sigt ID Unlimited - skiftet er en yml-indstilling.
    // Vendor-whitelabels overstyrer fortsat alt dette pr. organisation.
    return {
      colorHex: "#7BBB58",
      logoUrl: "",
      whiteLabelContactEmail: serverBrand?.email || enviroment.brandEmail,
      whiteLabelContactPhone: serverBrand?.phone || enviroment.brandPhone,
      whiteLabelCopyrightName: serverBrand?.name || enviroment.brandName,
      whiteLabelVendorName: serverBrand?.name || enviroment.brandName,
      whiteLabelWebsiteLink: serverBrand?.website || enviroment.brandWebsite,
    };
  }

  function getWhiteLabelWithDefaults(
    config: WhiteLabelConfigurations | undefined
  ): WhiteLabelConfigurations {
    const defaults = defaultConfigurations();
    if (!config) return defaults;
    return {
      colorHex: config.colorHex || defaults.colorHex,
      logoUrl: config.logoUrl || defaults.logoUrl,
      whiteLabelContactEmail:
        config.whiteLabelContactEmail || defaults.whiteLabelContactEmail,
      whiteLabelContactPhone:
        config.whiteLabelContactPhone || defaults.whiteLabelContactPhone,
      whiteLabelCopyrightName:
        config.whiteLabelCopyrightName || defaults.whiteLabelCopyrightName,
      whiteLabelVendorName:
        config.whiteLabelVendorName || defaults.whiteLabelVendorName,
      whiteLabelWebsiteLink:
        config.whiteLabelWebsiteLink || defaults.whiteLabelWebsiteLink,
    };
  }

  const whiteLabel = getWhiteLabelWithDefaults(
    profile?.whiteLabelConfigurations
  );

  useEffect(() => {
    setCurrentWhiteLabel(whiteLabel);
    // serverBrand is a dep: the brand query resolves AFTER mount (and changes
    // again when the admin saves in the brand editor) — without it the state
    // here keeps the mount-time snapshot (env defaults) forever, and the
    // server-stored brand never reaches header/footer.
  }, [profile, serverBrand]);

  const [currentWhiteLabel, setCurrentWhiteLabel] =
    useState<WhiteLabelConfigurations>(whiteLabel);

  function reset() {
    setCurrentWhiteLabel({ ...profile!.whiteLabelConfigurations });
  }

  function updateWhiteLabel(value: WhiteLabelConfigurations) {
    const test = { ...value };
    setCurrentWhiteLabel(test);
  }

  return (
    <WhiteLabelContext.Provider
      value={{
        whiteLabel: currentWhiteLabel,
        updateWhiteLabelContext: updateWhiteLabel,
        reset: reset,
      }}
    >
      {children}
    </WhiteLabelContext.Provider>
  );
}

function useWhiteLabelContext(): WhiteLabelProviderContext {
  const context = useContext(WhiteLabelContext);
  if (context === undefined) {
    throw new Error(
      "useRepositoriesContext must be used within a RepositoriesProvider"
    );
  }
  return context;
}

export { WhiteLabelProvider, useWhiteLabelContext };
